import { AUTH_CONNECTION } from "@web3auth/auth";
import { jwtDecode } from "jwt-decode";

import { clearPasswordlessIdToken, getPasswordlessIdToken, getStoredAuthConnection, setPasswordlessIdToken } from "@/store/modules/authStorage";

import { isStaleIssuer } from "./issuer";
import { promisifyWebAuthCheckSession } from "./passwordlessClient";

// Proactive passwordless renewal: auth0-js has no refresh tokens, only checkSession() vs the SSO cookie.
const PASSWORDLESS_RENEW_SKEW_MS = 60_000;
// setTimeout stores its delay as a signed 32-bit int, so the max safe value is
// 2^31 - 1 ms (~24.8 days). Larger delays overflow and fire immediately, so we clamp.
const SET_TIMEOUT_MAX_MS = 2_147_483_647;

let renewTimer: ReturnType<typeof setTimeout> | null = null;
let passwordlessAutoRenewInitialized = false;

function getTokenExpMs(idToken: string): number | null {
  try {
    const { exp } = jwtDecode<{ exp?: number }>(idToken);
    if (!exp) return null;
    return exp * 1000;
  } catch {
    return null;
  }
}

function isTokenExpired(idToken: string): boolean {
  const expMs = getTokenExpMs(idToken);
  if (!expMs) return true;
  return Date.now() >= expMs;
}

function isPasswordlessTokenValid(idToken: string): boolean {
  return !isTokenExpired(idToken) && !isStaleIssuer(idToken);
}

export function cancelScheduledRenewal(): void {
  if (renewTimer !== null) {
    clearTimeout(renewTimer);
    renewTimer = null;
  }
}

export function scheduleProactiveRenewal(idToken: string): void {
  cancelScheduledRenewal();
  const expMs = getTokenExpMs(idToken);
  if (!expMs) return;

  // Renew slightly before exp so API calls never send a stale bearer token.
  const delay = Math.min(Math.max(0, expMs - Date.now() - PASSWORDLESS_RENEW_SKEW_MS), SET_TIMEOUT_MAX_MS);
  renewTimer = setTimeout(() => {
    renewTimer = null;
    void proactiveRenew();
  }, delay);
}

async function proactiveRenew(): Promise<void> {
  if (!shouldAttemptPasswordlessRenewal()) {
    cancelScheduledRenewal();
    return;
  }
  await renewPasswordlessSession();
}

export function isPasswordlessAuthConnection(authConnection?: string): boolean {
  return authConnection === AUTH_CONNECTION.EMAIL_PASSWORDLESS;
}

// Passwordless (auth0-js) and social (SPA SDK) use separate token stores. Only treat
// sessionStorage as the active session when the stored auth connection is passwordless;
// otherwise a leftover OTP token would override getIdToken() / isAuthenticated() after social login.
export function shouldAttemptPasswordlessRenewal(authConnection?: string): boolean {
  return isPasswordlessAuthConnection(authConnection ?? getStoredAuthConnection());
}

/** Silently renew via auth0-js checkSession (not SPA SDK) when the Auth0 SSO cookie is still valid. */
async function renewPasswordlessSession(): Promise<string | null> {
  const authResult = await promisifyWebAuthCheckSession();
  if (!authResult?.idToken || !isPasswordlessTokenValid(authResult.idToken)) {
    return null;
  }
  setPasswordlessIdToken(authResult.idToken);
  scheduleProactiveRenewal(authResult.idToken);
  return authResult.idToken;
}

export async function getValidPasswordlessIdToken(authConnection?: string): Promise<string | null> {
  if (!shouldAttemptPasswordlessRenewal(authConnection)) {
    // User switched to social login; discard any stale passwordless token.
    if (getPasswordlessIdToken()) {
      cancelScheduledRenewal();
      clearPasswordlessIdToken();
    }
    return null;
  }

  const passwordlessToken = getPasswordlessIdToken();
  if (passwordlessToken && isPasswordlessTokenValid(passwordlessToken)) {
    return passwordlessToken;
  }
  if (passwordlessToken) {
    cancelScheduledRenewal();
    clearPasswordlessIdToken();
  }
  return renewPasswordlessSession();
}

/** Bootstrap passwordless auto-renew: schedule renewal before exp and re-check on tab focus. */
export function initPasswordlessAutoRenew(): void {
  if (!shouldAttemptPasswordlessRenewal()) {
    return;
  }

  const token = getPasswordlessIdToken();
  if (token && isPasswordlessTokenValid(token)) {
    scheduleProactiveRenewal(token);
  } else {
    void proactiveRenew();
  }

  if (passwordlessAutoRenewInitialized) {
    return;
  }
  passwordlessAutoRenewInitialized = true;

  // Timers often don't fire while the machine sleeps; re-check when the tab becomes visible.
  const onFocus = () => {
    if (document.visibilityState === "hidden") {
      return;
    }
    void getValidPasswordlessIdToken();
  };
  document.addEventListener("visibilitychange", onFocus);
  window.addEventListener("focus", onFocus);
}
